import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import type { RegisterBody, RegisterResponse } from '@lira/shared';
import { ApiError, apiPost } from '@/lib/api';
import { AuthCard, AuthField, AuthSubmit } from './AuthCard';

/** Cadastro aberto: nome, e-mail, WhatsApp e senha. A conta nasce pendente de aprovação. */
export function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;
    setError(null);

    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 13) {
      setError('Informe um WhatsApp válido com DDD.');
      return;
    }
    if (password.length < 8) {
      setError('A senha precisa de pelo menos 8 caracteres.');
      return;
    }
    if (password !== confirm) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);
    try {
      const body: RegisterBody = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: digits,
        password,
      };
      await apiPost<RegisterResponse>('/auth/register', body);
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Não foi possível criar a conta. Tente novamente.',
      );
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthCard>
        <div className="mt-8 flex flex-col items-center text-center">
          <span className="grid size-14 place-items-center rounded-full bg-income-weak text-income">
            <CheckCircle2 className="size-8" />
          </span>
          <h2 className="mt-4 text-lg font-semibold text-ink">Conta criada!</h2>
          <p className="mt-2 text-sm text-muted">
            Seu cadastro está <strong className="text-ink">aguardando aprovação</strong> do
            administrador. Você poderá entrar assim que for aprovado.
          </p>
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="mt-6 h-11 w-full rounded-full bg-primary font-semibold tracking-wide text-white transition-colors hover:bg-primary-hover"
          >
            Voltar para o login
          </button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <form onSubmit={(e) => void handleSubmit(e)} className="mt-8 space-y-4" noValidate>
        <AuthField
          label="Nome"
          id="name"
          type="text"
          autoComplete="name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <AuthField
          label="E-mail"
          id="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <AuthField
          label="WhatsApp (com DDD)"
          id="phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="(11) 99999-9999"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <AuthField
          label="Senha"
          id="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <AuthField
          label="Confirmar senha"
          id="confirm"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />

        {error && <p className="text-sm text-expense">{error}</p>}

        <AuthSubmit loading={loading}>CRIAR CONTA</AuthSubmit>
      </form>

      <p className="mt-6 text-center text-sm text-muted">
        <Link to="/login" className="font-medium text-primary hover:underline">
          Já tenho conta
        </Link>
      </p>
    </AuthCard>
  );
}
